import React, { useEffect, useMemo, useState } from 'react';
import './publisher.css';

const usePublisher = () => {
  const [loading, setLoading] = useState(true);
  const [publisher, setPublisher] = useState(null);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [meRes, prodRes] = await Promise.all([
        fetch('/api/store/publisher/me'),
        fetch('/api/store/publisher/products')
      ]);
      const me = await meRes.json();
      const list = await prodRes.json();
      setPublisher(me.publisher || null);
      setProducts(list.products || []);
    } catch (e) {
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);
  return { loading, publisher, products, error, refresh };
};

const FileUpload = ({ onUploaded, userId, label, accept, placeholder }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr(null);
    try {
      // Request a signed upload URL
      const ext = file.name.split('.').pop();
      const filePath = `${label.toLowerCase().replace(/\s+/g,'-')}-${Date.now()}.${ext}`;
      const path = `publisher/${userId}/${filePath}`;
      const resp = await fetch('/api/store/publisher/uploads/sign', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path })
      });
      const json = await resp.json();
      if (!resp.ok || !json?.url) throw new Error(json?.message || 'Failed to get upload url');

      // Upload using signed URL
      const uploadResp = await fetch(json.url, {
        method: 'PUT', headers: { 'Content-Type': file.type }, body: file
      });
      if (!uploadResp.ok) throw new Error('Upload failed');

      onUploaded({ path: json.path, type: file.type, size: file.size });
    } catch (e2) {
      setErr(e2.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="file-upload">
      <label className="label">{label}</label>
      <input type="file" accept={accept} onChange={onChange} disabled={busy} />
      {placeholder && <div className="hint">{placeholder}</div>}
      {busy && <div className="hint">Uploading...</div>}
      {err && <div className="error">{err}</div>}
    </div>
  );
};

const DraftCreator = ({ publisherId, onCreated }) => {
  const [form, setForm] = useState({
    slug: '', title: '', subtitle: '', description: '', language: '', level: '', category_slug: '', cover_url: ''
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setSaving(true); setErr(null);
    try {
      const resp = await fetch('/api/store/publisher/products', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json?.message || 'Failed to create draft');
      onCreated();
    } catch (e) {
      setErr(e.message || 'Failed to create draft');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="draft glass">
      <h3>Create Draft Product</h3>
      <div className="grid">
        <input placeholder="slug" value={form.slug} onChange={e=>setForm({...form, slug:e.target.value})} />
        <input placeholder="title" value={form.title} onChange={e=>setForm({...form, title:e.target.value})} />
        <input placeholder="subtitle" value={form.subtitle} onChange={e=>setForm({...form, subtitle:e.target.value})} />
        <input placeholder="language" value={form.language} onChange={e=>setForm({...form, language:e.target.value})} />
        <input placeholder="level" value={form.level} onChange={e=>setForm({...form, level:e.target.value})} />
        <input placeholder="category slug (e.g., grammar)" value={form.category_slug} onChange={e=>setForm({...form, category_slug:e.target.value})} />
        <input placeholder="cover url (from uploaded)" value={form.cover_url} onChange={e=>setForm({...form, cover_url:e.target.value})} />
        <textarea placeholder="description" value={form.description} onChange={e=>setForm({...form, description:e.target.value})} />
      </div>
      {err && <div className="error">{err}</div>}
      <div className="actions">
        <button className="btn-neon" onClick={submit} disabled={saving}>{saving?'Creating...':'Create Draft'}</button>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { loading, publisher, products, error, refresh } = usePublisher();
  const userId = useMemo(() => publisher?.user_id, [publisher]);

  const submitForReview = async (id) => {
    const res = await fetch(`/api/store/publisher/products/${id}/submit`, { method: 'POST' });
    const json = await res.json();
    if (res.ok && json?.success) {
      await refresh();
      alert('Submitted for review');
    } else {
      alert(json?.message || 'Submit failed');
    }
  };

  if (loading) return <div className="publisher glass"><div className="loading">Loading...</div></div>;
  if (error) return <div className="publisher glass"><div className="error">{error}</div></div>;
  if (!publisher) return <div className="publisher glass"><div className="error">No publisher profile. <a href="/eBookstore/publisher/onboarding">Go to Onboarding</a></div></div>;

  return (
    <div className="publisher">
      <div className="header">
        <h1>Publisher Dashboard</h1>
        <div className={`status badge ${publisher.status}`}>Status: {publisher.status}</div>
      </div>

      <div className="glass section">
        <h3>Upload Assets (staging)</h3>
        <div className="grid2">
          <FileUpload label="Front Cover" accept="image/*" userId={userId} onUploaded={(f)=>alert('Uploaded: '+f.path)} />
          <FileUpload label="Back Cover" accept="image/*" userId={userId} onUploaded={(f)=>alert('Uploaded: '+f.path)} />
          <FileUpload label="Sample PDF" accept="application/pdf" userId={userId} onUploaded={(f)=>alert('Uploaded: '+f.path)} />
          <FileUpload label="Full Ebook" accept="application/pdf,application/epub+zip,application/zip" userId={userId} onUploaded={(f)=>alert('Uploaded: '+f.path)} />
        </div>
        <div className="hint">Copy the uploaded URLs into your draft product fields as needed.</div>
      </div>

      <DraftCreator publisherId={publisher.id} onCreated={refresh} />

      <div className="glass section">
        <h3>Your Products</h3>
        <div className="table">
          <div className="row head"><span>Title</span><span>Slug</span><span>Status</span><span>Action</span></div>
          {(products||[]).map(p => (
            <div className="row" key={p.id}><span>{p.title}</span><span>{p.slug}</span><span>{p.link_status||p.status}</span><span>
              {p.link_status==='draft' && <button className="btn-neon" onClick={()=>submitForReview(p.id)}>Submit</button>}
            </span></div>
          ))}
          {products.length===0 && <div className="row"><span colSpan={4}>No products yet.</span></div>}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
